import { useState } from "react";
import { useApiClient } from "../api/ApiContext.jsx";
import { useApi } from "../hooks/useApi.js";
import ReliabilityReport from "./ReliabilityReport.jsx";

/**
 * ReliabilityPanel
 *
 * The data half of the reliability view: it owns the chosen window, fetches the
 * report for its scope, and hands both to the presentational component.
 *
 * **Why the window is state here and not in the URL.** Switching 1h → 24h is a
 * glance, not a navigation: putting it in the query string would make the back
 * button step through windows instead of leaving the screen. The chosen window is
 * still passed down rather than held by the report, because the fetch depends on
 * it — one owner for the value that decides what is being asked for.
 *
 * **Why the previous report stays on screen while the next one loads.** `useApi`
 * keeps the last payload through a reload, and blanking the cards on every switch
 * would make a one-second window change throw away the numbers the reader was
 * looking at. The stale report is marked as updating instead, so it is never
 * mistaken for the new window's answer.
 */
export default function ReliabilityPanel({ scope = "project", projectId, onOpenFeed }) {
  const api = useApiClient();
  const [windowToken, setWindowToken] = useState("24h");

  const query = useApi(
    () =>
      scope === "admin"
        ? api.admin.reliability({ window: windowToken })
        : api.projects.reliability(projectId, { window: windowToken }),
    [api, scope, projectId, windowToken],
  );

  return (
    <ReliabilityReport
      report={query.data}
      windowToken={windowToken}
      onWindow={setWindowToken}
      scope={scope}
      // The first load has nothing to show; a window change has the old numbers.
      loading={query.loading && !query.data}
      refreshing={query.loading && Boolean(query.data)}
      error={query.error}
      onOpenFeed={onOpenFeed}
    />
  );
}
